import * as React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import styled, { css } from 'styled-components';
import { InputStyleMode, textInputPadding } from './TextInput';
import { defaultInputWrapperCss, disabledInputWrapperCss, errorStateInputWrapperCss } from './sharedContainerStyles';

import { colors } from '../colors/color';

export type TextAreaProps = {
    innerRef?:React.LegacyRef<TextareaAutosize> & React.LegacyRef<HTMLTextAreaElement>
    id?: string,
    value: string,
    placeholder?:string,
    minRows?:number,
    disabled?: boolean,
    inErrorState?:boolean,
    tabIndex?: number,
    autoFocus?: boolean,
    dataCy?:string,
    onChange: (value: string) => void;
    onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void,
    onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void,
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void,
    styleMode?:InputStyleMode,
    ariaLabel?:string,
    fontSize?: 14 | 16
}

export function TextArea(props: TextAreaProps): JSX.Element 
{
    //const [focus, setFocus] = React.useState(false);

    function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
        props.onChange(event.target.value);
    }

    function handleFocus(e:React.FocusEvent<HTMLTextAreaElement>)
    {
        //setFocus(true);
        if(props.onFocus)
            props.onFocus(e);
    }

    function handleBlur(e:React.FocusEvent<HTMLTextAreaElement>)
    {
        //setFocus(false);
        if(props.onBlur)
            props.onBlur(e);
    }

    const disabled = props.disabled === undefined ? false : props.disabled;
    const inErrorState = props.inErrorState === undefined ? false : props.inErrorState;

    const { paddingVertical, paddingHorizontal } = textInputPadding(props.fontSize);

    return (
        <TextAreaWrapper 
            disabled={disabled} 
            inErrorState={inErrorState}
            fontSize={props.fontSize || 16} 
            paddingVertical={paddingVertical} 
            paddingHorizontal={paddingHorizontal} 
            styleMode={props.styleMode || 'default'}>
            <TextareaAutosize
                data-cy={props.dataCy}
                autoFocus={props.autoFocus}
                id={props.id}
                value={props.value}
                placeholder={props.placeholder}
                minRows={props.minRows}
                disabled={props.disabled}
                tabIndex={props.tabIndex}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={props.onKeyDown}
                ref={props.innerRef}
                aria-label={props.ariaLabel}
                name={"textArea"}
                />
                
        </TextAreaWrapper>
    );
}



const commonInputWrapper = css<{disabled:boolean, inErrorState:boolean}>`
    background: white;
    display: flex;
    flex-direction: row;
    gap:0;
    border-radius: 4px;
    min-height: 40px;
    

    & > textarea 
    {
        font-family: "Noto Sans", sans-serif;
        width:100%;
        background: transparent;
        outline: none;
        border:none;
        box-sizing: border-box;
        color:${props => props.disabled
             ? colors.inputState.disabled.text
             : (props.inErrorState 
                 ? colors.inputState.error.text 
                 : colors.text.normal
             )
         };
        resize: none;
        word-wrap:break-word;
        line-height: 20px;
    }


    & > textarea::placeholder{
        color:${colors.text.inactive};
    }

    
    ${props => props.disabled
        ? disabledInputWrapperCss
        : (props.inErrorState ? errorStateInputWrapperCss : defaultInputWrapperCss)
    }
    
`;

const defaultInputWrapper = css<{disabled:boolean, inErrorState:boolean, fontSize:number, paddingVertical:number, paddingHorizontal:number}>`
    ${commonInputWrapper};

    
    & > input, & > textarea {
        font-size: ${props => props.fontSize}px;      
        padding: ${props => props.paddingVertical}px ${props => props.paddingHorizontal}px;
    }
`;

const TextAreaWrapper = styled.div<{disabled:boolean, inErrorState:boolean, fontSize:number, paddingVertical:number, paddingHorizontal:number, styleMode:InputStyleMode}>`
    ${defaultInputWrapper}
`;